import classnames from 'classnames/bind'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { PartialBy } from '~/@types/generic'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { SHOPIFY_CUSTOM_ATTRIBUTES, TRACKING_EVENTS } from '~/lib/constants'

import { UnlikelyCartLineInput } from '@unlikelystudio/commerce-connector'

import LoadingComponent from '~/components/Abstracts/LoadingComponent'
import Ratio from '~/components/Abstracts/Ratio'
import { useAddToCart } from '~/components/AddToCart'
import { UpsalesSliderProps } from '~/components/Cart/UpsalesSlider'
import BlurredCta from '~/components/UI/BlurredCta'
import { CardProductProps } from '~/components/UI/CardProduct'
import Image, { ImageProps } from '~/components/UI/Image'

import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import { fromProductCardPropsToProductTrackingData } from '~/hooks/tracking/useTrackProductCardsList'
import useLocale from '~/hooks/useLocale'

import { CTA } from '~/data/dictionary'
import serializePrice from '~/data/serialize-price'

import css from './styles.module.scss'

const cx = classnames.bind(css)

interface UpsaleItemProps extends PartialBy<CardProductProps, 'image'> {
  className?: string
  image?: ImageProps
  isFetching?: boolean
  layout?: UpsalesSliderProps['layout']
}

const IMAGE_SIZES = [{ ratio: 495 / 330 }]

function UpsaleItem({
  className,
  name,
  image,
  defaultVariantId,
  defaultVariantTitle,
  slug,
  defaultVariant,
  productName,
  meanings,
  categories,
  shopifyId,
  compareAtPrice,
  isFetching,
  layout,
  link,
}: UpsaleItemProps) {
  const tracker = useTracker()
  const { PRODUCT_ADD_TO_CART } = TRACKING_EVENTS
  const t = useTranslate()
  const locale = useLocale()
  const getUpsaleTrackingData = () => {
    return fromProductCardPropsToProductTrackingData(
      {
        name: productName ?? null,
        categories: categories ?? null,
        meanings: meanings ?? null,
        shopifyId: shopifyId ?? null,
        defaultVariant: defaultVariant ?? null,
        price: defaultVariant?.price,
        compareAtPrice: defaultVariant?.compareAtPrice,
        media: [{ image }],
        link,
      },
      {},
    )
  }

  const trackingData = getUpsaleTrackingData()
  const triggerEvent = (event: string) => {
    return tracker.emit<typeof event>(event, [trackingData])
  }

  const handleAddToCartTracking = () => triggerEvent(PRODUCT_ADD_TO_CART)

  const productsToAdd: UnlikelyCartLineInput[] = [
    {
      merchandiseId: defaultVariantId,
      quantity: 1,
      attributes: [
        {
          key: '_storyblok-slug',
          value: slug,
        },
        { key: '_sku', value: defaultVariant?.sku },
        {
          key: SHOPIFY_CUSTOM_ATTRIBUTES.trackingData,
          value: JSON.stringify(trackingData),
        },
      ],
    },
  ]

  const { mutate, isLoading } = useAddToCart({
    withToast: false,
    onSuccess: () => handleAddToCartTracking(),
  })
  const priceTextStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const compareAtPriceTextStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Secondary,
  })

  const variantTitleStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Secondary,
    color: GlobalThemeColors.Gray1ToBlack,
  })

  return (
    <div
      className={cx(className, css.UpsellItem, css?.[`layout-${layout}`], {
        isFetching,
      })}>
      <div className={cx(css.wrapper)}>
        {isFetching && <LoadingComponent className={cx(css.loader)} />}
        <div className={css.cardTop}>
          {image && (
            <Image
              className={css.image}
              sizesFromBreakpoints={IMAGE_SIZES}
              ratio={0.77777778}
              draggable={false}
              layout="fill"
              objectFit="cover"
              asPlaceholder
              {...image}
            />
          )}
          {isFetching && (
            <Ratio className={css.image} ratio={0.7777778}>
              {() => null}
            </Ratio>
          )}
        </div>
        <div className={css.cardBottom}>
          {isFetching && (
            <>
              <div className={cx(css.content)}>
                <div className={cx(css.titleContainer)}>
                  <span className={cx(css.name, priceTextStyle)}>&nbsp;</span>
                  <span className={cx(css.variantTitle, variantTitleStyle)}>
                    &nbsp;
                  </span>
                </div>
                {
                  <div className={cx(css.priceContainer)}>
                    <span className={priceTextStyle}>&nbsp;</span>
                  </div>
                }
              </div>
              <div className={css.cta}></div>
            </>
          )}
          {!isFetching && (
            <>
              <div className={cx(css.content)}>
                <div className={cx(css.titleContainer)}>
                  {name && (
                    <span className={cx(css.name, priceTextStyle)}>{name}</span>
                  )}
                  {defaultVariantTitle && (
                    <span className={cx(css.variantTitle, variantTitleStyle)}>
                      {defaultVariantTitle}
                    </span>
                  )}
                </div>
                {
                  <div className={cx(css.priceContainer)}>
                    {compareAtPrice && (
                      <del
                        className={cx(
                          css.compareAtPrice,
                          compareAtPriceTextStyle,
                        )}>
                        {serializePrice(
                          locale,
                          compareAtPrice.currencyCode,
                          compareAtPrice.amount,
                        )}
                      </del>
                    )}
                    {defaultVariant.price && (
                      <span className={priceTextStyle}>
                        {serializePrice(
                          locale,
                          defaultVariant.price.currencyCode,
                          defaultVariant.price.amount,
                        )}
                      </span>
                    )}
                  </div>
                }
              </div>
              <BlurredCta
                className={css.cta}
                textPreset={GlobalTextPreset.Cta10Primary}
                isLoading={isLoading}
                onClick={() => {
                  mutate(productsToAdd)
                }}
                theme={GlobalThemeColors.Shade1}>
                {t(CTA.ADD_TO_CART)}
              </BlurredCta>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default UpsaleItem
